import { HttpClient } from '@angular/common/http';
import { SearchResourceType } from '@app/core/model/search/search-resource-type';
import { BsApiEndPoints } from '@app/core/services/bs-api/endpoints';
import { ISearchResourceConfig } from '@app/core/services/resource-dictionary/search-resource-dictionary-types';
import { SearchResourceDictionaryService } from '@app/core/services/resource-dictionary/search-resource-dictionary.service';
import { DataTableUtilities } from '@app/core/utilities/data-table-utilities';
import { BytesFilter } from '@app/core/utilities/filters/bytes.filter';
import { BsshDatePipe } from '@app/core/utilities/pipes/bssh-date.pipe';
import { CustomColDef } from '@app/shared/bssh-data-table/custom-colDef';
import { RadLinkRendererComponent } from '@app/shared/cell-renderers/rad-link-renderer/rad-link-renderer.component';
import { FilterOption } from '@bssh/comp-lib/lib/components/data-table/custom-colDef';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ColumnMinWidth, OverrideColumnDefinition, OverrideColumnParameters, SearchGridOptionsFormatter } from './search-grid-options-formatter';

export enum SampleSearchColumnFields {
    Name = 'Name',
    Created = 'Created',
    Owner = 'Owner',
    Project = 'Project',
    Size = 'Size',
    ReadLength = 'ReadLength',
    ReadLength1 = 'ReadLength1',
    ReadLength2 = 'ReadLength2',
    NumReadsPF = 'NumReadsPF',
    Genome = 'Genome'
}

export class SampleSearchGridOptionsFormatter extends SearchGridOptionsFormatter<SampleSearchColumnFields> {
    private sampleResourceConfig: ISearchResourceConfig;
    public projectFilterOptions$: Observable<FilterOption[]>;
    public genomeFilterOptions$: Observable<FilterOption[]>;
    public projectColumnFilterOptionsUpdateCallback;

    constructor(
      public resourceDictionaryService: SearchResourceDictionaryService, 
      public bsshDatePipe: BsshDatePipe,
      public http: HttpClient
    ) {
        super(resourceDictionaryService, SearchResourceType.Sample);
        this.sampleResourceConfig = this.resourceDictionaryService.getResourceConfigByType(SearchResourceType.Sample);
        this.bindFilterUpdateCallbacksToCurrentContext();
        this.projectFilterOptions$ = this.updateProjectColumnFilterOptions();
        this.genomeFilterOptions$ = this.getGenomeFilterOptions();
    }

    getGenomeFilterOptions(): Observable<any> {
      return this.http.get(BsApiEndPoints.GENOME_FILTER, {
            params: {
                limit: '1000',
                sortBy: 'SpeciesName',
                sortDir: 'asc',
            },
            withCredentials: true
        }).pipe(
          map((response) => {
            return this.toGenomeFilterOptions(response);
          })
        )
    }

    getProjectFilterOptions(arg?: string): Observable<any> {
        return this.http.get(BsApiEndPoints.SEARCH_FILTER_URL_V1PRE3, {
            params: {
                limit: '50',
                scope: 'projects',
                sortBy: 'ModifiedOn',
                sortDir: 'desc',
                query: arg ? `Project.Name:"${arg}"` : ''
            },
            withCredentials: true
        });
    }

    updateProjectColumnFilterOptions(arg?: string): Observable<FilterOption[]> {
        return this.getProjectFilterOptions(arg).pipe(
          map((response) => {
            return this.toProjectFilterOptions(response);
          })
        );
    }

    bindFilterUpdateCallbacksToCurrentContext() {
        this.projectColumnFilterOptionsUpdateCallback = this.updateProjectColumnFilterOptions.bind(this);
    }

    toProjectFilterOptions(result): FilterOption[] {
        const projects: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        projects.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.Project.Name,
            filterValue: item.Project.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    toGenomeFilterOptions(result): FilterOption[] {
        const genomes: [] = result.Response.Items;
        const filteroptions: FilterOption[] = [];
        genomes.forEach((item: any) => {
          const filterOption: FilterOption = {
            displayName: item.DisplayName,
            filterValue: item.Id
          };
          filteroptions.push(filterOption);

        });
        return filteroptions;
    }

    protected getOverrideColumnParameters(): OverrideColumnParameters<SampleSearchColumnFields> {
        const inclusiveColumns: SampleSearchColumnFields[] = [
            SampleSearchColumnFields.Name,
            SampleSearchColumnFields.Project,
            SampleSearchColumnFields.Owner,
            SampleSearchColumnFields.Created
        ];

        const overrideColumnDefinitions: OverrideColumnDefinition<SampleSearchColumnFields>[] = [
            {
              colId: SampleSearchColumnFields.Name,
              checkboxSelection: false
            }
        ];

        return {
            inclusiveColumns,
            overrideColumnDefinitions
        };
    }

    protected getDefaultColumnDefinitions(): CustomColDef[] {
        const sampleConfig = this.sampleResourceConfig;
        return [
            {
                headerName: 'Name',
                field: 'Sample.Name',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data) {
                      const tooltipText = !this.isRowSelectable(params.node) ? this.getUnselectableRowMessage(params.node) : null;
                        return DataTableUtilities.getRadLinkParams(
                            params.data.Sample.Name,
                            sampleConfig.uiHrefAccessor(params.data),
                            tooltipText,
                            '_blank',
                            60
                        );
                    } else {
                        return {};
                    }
                },
                flex: 3.5,
                minWidth: ColumnMinWidth.RESOURCE,
                filterActive: false,
                colId: SampleSearchColumnFields.Name,
                sortable: false,
                resizable: true,
            },
              {
                headerName: 'Created',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return this.bsshDatePipe.transform(params.data.Sample.DateCreated);
                  },
                flex: 1.5,
                minWidth: ColumnMinWidth.DATE,
                filterActive: false,
                colId: SampleSearchColumnFields.Created,
                cellRenderer: 'span',
                resizable: true,
                sortable: false
              },
              {
                headerName: 'Owner',
                field: 'Sample.UserOwnedBy.Name',
                flex: 2,
                minWidth: ColumnMinWidth.RESOURCE,
                colId: SampleSearchColumnFields.Owner,
                cellRenderer: 'span',
                sortable: false,
                resizable: true,
              },
              {
                headerName: 'Project',
                cellRendererFramework: RadLinkRendererComponent,
                cellRendererParams: (params) => {
                    if(params.data && params.data.Sample && params.data.Sample.Projects && params.data.Sample.Projects[0]) {
                        const project = params.data.Sample.Projects[0];
                        return DataTableUtilities.getRadLinkParams(
                            project.Name,
                            `/projects/${project.Id}`
                        );
                    } else {
                        return {};
                    }
                },
                flex: 3,
                minWidth: ColumnMinWidth.RESOURCE,
                colId: SampleSearchColumnFields.Project,
                sortable: false,
                filterActive: true,
                filterWithSearchBox: true,
                filterOptions: this.projectFilterOptions$,
                updateFilterOptionsCallback: this.projectColumnFilterOptionsUpdateCallback,
              },
              {
                headerName: 'Size',
                valueGetter: (params) => {
                    if (!params.data) { return; }
                    return BytesFilter(params.data.Sample.TotalSize);
                },
                cellRenderer: 'span',
                suppressSizeToFit: true,
                resizable: true,
                minWidth: ColumnMinWidth.SIZE,
                maxWidth: 120,
                colId: SampleSearchColumnFields.Size,
                sortable: false
              },
              {
                headerName: 'Read Length',
                field: SampleSearchColumnFields.ReadLength,
                colId: SampleSearchColumnFields.ReadLength,
                valueGetter: params => {
                  if (params.data) {
                    const { Read1, Read2 } = params.data.Sample;
                    return `${Read1} | ${Read2}`;
                  }
                },
                cellRenderer: 'span',
                minWidth: 160,
                resizable: true,
                sortable: false,
                filterActive: true,
                filter: 'agNumberColumnFilter',
                filterParams: {
                  filterOptions: ['inRange'],
                  suppressAndOrCondition: true,
                }
              },
              {
                headerName: 'Read Length 1',
                field: 'Sample.Read1',
                colId: SampleSearchColumnFields.ReadLength1,
                cellRenderer: 'span',
                minWidth: 180,
                resizable: true,
                sortable: false,
                numberRangeFilterActive: true,
              },
              {
                headerName: 'Read Length 2',
                field: 'Sample.Read2',
                colId: SampleSearchColumnFields.ReadLength2,
                cellRenderer: 'span',
                minWidth: 180,
                resizable: true,
                sortable: false,
                numberRangeFilterActive: true,
              },
              {
                headerName: 'Reads PF',
                field: 'Sample.NumReadsPF',
                colId: SampleSearchColumnFields.NumReadsPF,
                cellRenderer: 'span',
                minWidth: 160,
                resizable: true,
                sortable: false,
                numberRangeFilterActive: true,
              },
              {
                headerName: 'Genome',
                field: 'Sample.Genome.DisplayName',
                colId: SampleSearchColumnFields.Genome,
                cellRenderer: 'span',
                minWidth: 160,
                resizable: true,
                sortable: false,
                filterActive: true,
                filterOptions: this.genomeFilterOptions$,
              }
        ];
    }
}